import { KeyboardEvent, RefObject, useEffect, useState } from 'react';

export const handleTrapFocus = (
  event: KeyboardEvent<HTMLElement>,
  ref: RefObject<HTMLElement>,
  callback: VoidFunction
) => {
  if (event.code === 'Escape') {
    callback();
  }
  // Check for tab key - not present return
  if (event.code !== 'Tab') {
    return;
  }

  // Query all focusable elements in the ref
  const focusableElements = ref.current?.querySelectorAll(
    'button:not([disabled]), button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [tabindex = "0"], details:not([disabled]), summary:not(:disabled)'
  );

  if (focusableElements) {
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // If moving forward using tab, and lastElement is active, move focus to first focusable element
    if (!event.shiftKey && document.activeElement === lastElement) {
      (firstElement as HTMLElement)?.focus();
      return event.preventDefault();
    }

    // If going backward by pressing tab + shift, and firstElement is active, move focus to last focusable element
    if (event.shiftKey && document.activeElement === firstElement) {
      (lastElement as HTMLElement)?.focus();
      event.preventDefault();
    }
  }
};

// There are situations in which one modal opens another modal and it might be hard to capture a keyboard event.
// This is mainly useful for triggering handleTrapFocus.
export const focusFirstElement = (ref: RefObject<HTMLElement>) => {
  const focusableElements = ref.current?.querySelectorAll(
    'button:not([disabled]), button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [tabindex = "0"], details:not([disabled]), summary:not(:disabled)'
  );

  if (focusableElements) {
    (focusableElements[0] as HTMLElement).focus();
  }
};

export const useActiveElement = () => {
  const [activeElement, setActiveElement] = useState(document.activeElement);

  const handleActiveElement = () => {
    setActiveElement(document.activeElement);
  };

  useEffect(() => {
    document.addEventListener('focusin', handleActiveElement);
    return () => {
      document.removeEventListener('focusin', handleActiveElement);
    };
  }, []);

  return activeElement;
};

export const clickCurrentTarget = (event: KeyboardEvent) => {
  if (event.code === 'Enter') {
    (event.currentTarget as HTMLElement)?.click();
  }
};

export const focusCurrentRef = (event: KeyboardEvent, ref: RefObject<HTMLElement>) => {
  if (event.code === 'Enter') {
    ref.current?.focus();
  }
};
